import { type JSX } from 'react';
import { useState, useEffect } from 'react';
import { useParams, Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import useFetch, { FetchConfig, Status } from '../../../../../hooks/fetch.hook';
import PageIntroduction from '../../../Shared/PageIntroduction';
import Spinner from '../../../../Shared/Spinner';
import Breadcumb from '../../../Shared/Breadcrumb';
import TabList from '../../../../Shared/TabList';
import TabListNavItem from '../../../../Shared/TabList/components/TabListNavItem';
import TabListTabContent from '../../../../Shared/TabList/components/TabListTabContent';
import Participations from './components/Participations';
import type { UserApiModel } from '../../../models/user.api-model';
import MunicipalityApiModel from '../../../models/municipality.api-model';
import type { IMunicipality } from '../../../../../models/municipality.model';
import { CompetitionApiModel } from '../../../models/competition.api-model';
import { SystemClock } from '../../../services/system-clock.service';
import type ICompetition from '../../../../../models/pa-egna-ben/competition.model';
import ParticipationListEntryApiModel from './models/participation-list-entry.api-model';
import type IParticipationListEntryViewModel from './models/participation-list-entry.view-model';
import SchoolApiModel from '../../../models/school.api-model';
import type ISchool from '../../../../../models/pa-egna-ben/school.model';
import { AnalyticsService } from '../../../../../services/analytics-service';
import Alert from '../../../../Shared/Alert';
import './style.scss';
import InformationBox from '../../../Shared/InformationBox';
import SelectedFilterValue from '../../../../Shared/SelectedFilterValue';
import ListItemDropDownMenu, { type MenuItem } from '../../../Shared/ListItemDropDownMenu';
import type IParticipation from '../../../../../models/pa-egna-ben/participation.model';
import { MenuItemType } from '../../../Shared/ListItemDropDownMenu/menu-item-type.enum';
import { InformationBoxType } from '../../../Shared/InformationBox/information-box-type.enum';
import { FilterTypes } from './filter-types.enum';
import AntiforgeryService from '../../../../../services/anti-forgery.service';

function processReceivedMunicipalityData(data: MunicipalityApiModel): IMunicipality | undefined {
  return data ? MunicipalityApiModel.mapToBusinessModel(data) : undefined;
}

function processReceivedCompetitionData(data: CompetitionApiModel): ICompetition | undefined {
  return data ? CompetitionApiModel.mapToBusinessModel(data) : undefined;
}

function processReceivedParticipationListingData(data: ParticipationListEntryApiModel[]): IParticipationListEntryViewModel[] {
  return data?.map((p) => ParticipationListEntryApiModel.mapToBusinessModel(p)) ?? [];
}

function processReceivedSchoolListingData(data: SchoolApiModel[]): ISchool[] {
  const schools = data?.map((p) => SchoolApiModel.mapToBusinessModel(p)) ?? [];
  return schools.sort((a, b) => a.name.localeCompare(b.name));
}

function Municipality() {
  // === HOOKS ===
  const { municipalityGid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // === STATE ===
  const [activeTabIndex, setActiveTabIndex] = useState(searchParams.get('t') === '1' ? 1 : 0);
  const [edited, setEdited] = useState(0);
  const [participationDeleted, setParticipationDeleted] = useState(0);
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [selectedLegNumber, setSelectedLegNumber] = useState<number | undefined>(undefined);
  const [{ status: getUserStatus, httpResponseStatusCode: getUserHttpResponseStatusCode }] = useFetch<UserApiModel[]>('/api/paegnaben/municipality-administrator/current');
  const [{ data: getCurrentCompetitionData }] = useFetch<CompetitionApiModel>('/api/paegnaben/municipality-administrator/competitions/current');
  const [{ status: getMunicipalityStatus, data: getMunicipalityData, httpResponseStatusCode: getMunicipalityHttpResponseStatusCode }] = useFetch<MunicipalityApiModel>(
    `/api/paegnaben/municipality-administrator/current/municipalities/${municipalityGid}`,
  );
  const [{ data: getParticipationsData, httpResponseStatusCode: getParticipationsHttpResponseStatusCode }] = useFetch<ParticipationListEntryApiModel[]>(
    `/api/paegnaben/municipality-administrator/current/municipalities/${municipalityGid}/participations?legNumber=${selectedLegNumber}`,
    selectedLegNumber === undefined,
    false,
    FetchConfig.default,
    [participationDeleted, selectedLegNumber],
  );
  const [{ status: getSchoolsStatus, data: getSchoolsData, httpResponseStatusCode: getSchoolsHttpResponseStatusCode }] = useFetch<SchoolApiModel[]>(
    `/api/paegnaben/municipality-administrator/municipality/${municipalityGid}/schools`,
    false,
    false,
    FetchConfig.default,
    [edited],
  );
  const [showNavItems] = useState(true);
  const [schoolBackup, setSchoolBackup] = useState<ISchool | undefined>(undefined);
  // Depending on the source of the data comes from, it can be either IParticipationListEntryViewModel or IParticipation. Delete can be done in participations component or in edit participation componen
  const [deletedParticipation, setDeletedParticipation] = useState<IParticipationListEntryViewModel | IParticipation | undefined>(undefined);

  const [selectedFilter, setSelectedFilter] = useState<FilterTypes>(FilterTypes.None);
  const [schoolFilter, setSchoolFilter] = useState<ISchool | undefined>(undefined);

  // === VARIABLES ===
  const now = SystemClock.now();
  const loadingDataInProgress = getUserStatus === Status.loading || getMunicipalityStatus === Status.loading;
  const currentCompetition = getCurrentCompetitionData ? processReceivedCompetitionData(getCurrentCompetitionData) : undefined;
  const municipality = getMunicipalityData ? processReceivedMunicipalityData(getMunicipalityData) : undefined;
  const participations = getParticipationsData ? processReceivedParticipationListingData(getParticipationsData) : undefined;
  const schools = getSchoolsData ? processReceivedSchoolListingData(getSchoolsData) : undefined;
  // Should be able to register from regisrtration start time and up to the start time of the first leg
  const isAllowedToRegister = currentCompetition?.registrationStartTime && currentCompetition?.registrationStartTime <= now && now < currentCompetition?.legs[0].startTime;

  const currentLeg = currentCompetition?.legs.find((leg) => leg.startTime <= now && now <= leg.endTime);
  const latestFinishedLegNumber = (currentLeg?.number ?? 0) - 1;
  const lastLegFinished = currentCompetition?.legs.length === 5 && currentCompetition.legs[4].endTime < now;
  const anyLegFinsihed = latestFinishedLegNumber > 0 || lastLegFinished;
  const firstLeg = currentCompetition?.legs.find((leg) => leg.number === 1);
  const anyLegIsActive = (firstLeg && firstLeg.startTime < now && currentCompetition?.endTime && now < currentCompetition?.endTime) || false;

  const participationsFilteredBySchoolOrAll = participations?.filter((p) => !schoolFilter || p.schoolId === schoolFilter.id);
  const participationsFilteredByMissingLegPoints = participationsFilteredBySchoolOrAll?.filter((p) => p.latestFinishedLegReportWeightedScore === 0 || !p.latestFinishedLegReport);
  const participationsFilteredByPassedQualification = participationsFilteredBySchoolOrAll?.filter(
    (p) => p.latestFinishedLegReportWeightedScore !== undefined && p.latestFinishedLegReportWeightedScore >= p.latestFinishedLegReportLimit,
  );

  const createdSchoolIdParam = searchParams.get('s');
  const createdSchoolId = createdSchoolIdParam ? parseInt(createdSchoolIdParam, 10) : undefined;
  const deletedSchoolIdParam = searchParams.get('ds');
  const deletedSchoolId = deletedSchoolIdParam ? parseInt(deletedSchoolIdParam, 10) : undefined;
  const savedSchoolIdParam = searchParams.get('ss');
  const savedSchoolId = savedSchoolIdParam ? parseInt(savedSchoolIdParam, 10) : undefined;

  const dpParam = searchParams.get('dp');
  const deletedParticipationId = dpParam ? parseInt(dpParam, 10) : undefined;
  const newlyRegisteredParticipationId = new URLSearchParams(location.search).get('id');
  const newlyRegisteredParticipation = participations?.find((p) => p.participationId.toString() === newlyRegisteredParticipationId);

  useEffect(() => {
    if (deletedSchoolId && location.state) {
      const deletedSchool = SchoolApiModel.mapToBusinessModel(location.state as SchoolApiModel);
      if (deletedSchool && deletedSchool !== schoolBackup) {
        searchParams.delete('ds');
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSchoolBackup(deletedSchool);
      }
    }
  }, [deletedSchoolId, schoolBackup, searchParams, location.state, setSearchParams]);

  useEffect(() => {
    if (deletedParticipationId && location.state) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDeletedParticipation(location.state as IParticipation);
      searchParams.delete('dp');
      setSearchParams(searchParams);
    }
  }, [deletedParticipationId, location.state, searchParams, setSearchParams]);

  useEffect(() => {
    if (selectedLegNumber === undefined) {
      if (currentLeg) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedLegNumber(currentLeg.number === 1 ? currentLeg.number : currentLeg.number - 1);
      } else if (lastLegFinished) {
        setSelectedLegNumber(5);
      } else if (isAllowedToRegister) {
        setSelectedLegNumber(0);
      }
    }
  }, [currentLeg, lastLegFinished, selectedLegNumber, isAllowedToRegister]);

  // === HELPER FUNCTIONS ===
  function getParticipationsWithCurrentFilter(): IParticipationListEntryViewModel[] {
    switch (selectedFilter) {
      case FilterTypes.PassedQualification:
        return participationsFilteredByPassedQualification ?? [];
      case FilterTypes.MissingLegPoints:
        return participationsFilteredByMissingLegPoints ?? [];
      default:
        return participationsFilteredBySchoolOrAll ?? [];
    }
  }

  function legOptionDisabled(legNumber: number): boolean {
    if (!anyLegIsActive && !lastLegFinished) return true;
    if (!currentLeg) return false;
    return legNumber > currentLeg.number;
  }

  function getLegOptionText(legNumber: number): string {
    let openLegText = '';
    if (currentLeg && legNumber === currentLeg.number) {
      openLegText = `(pågår t.o.m. ${currentLeg.endTime.toLocaleString({ day: 'numeric', month: 'numeric' })}) `;
    } else if ((currentLeg && legNumber > currentLeg.number) || (!anyLegIsActive && !lastLegFinished)) {
      const leg = currentCompetition?.legs.find((leg) => leg.number === legNumber);
      openLegText = leg ? `(startar ${leg.startTime.toLocaleString({ day: 'numeric', month: 'numeric' })})` : '';
    }
    return `Etapp ${legNumber.toString()} ${openLegText}`;
  }

  function getLegOptions(): JSX.Element[] {
    return (
      currentCompetition?.legs.map((leg) => (
        <option key={leg.number} value={leg.number} disabled={legOptionDisabled(leg.number)}>
          {getLegOptionText(leg.number)}
        </option>
      )) ?? []
    );
  }

  // === EVENT HANDLERS ===
  const handleLegSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLegNumber(parseInt(event.target.value, 10));
  };

  // === RENDER ===
  if (getMunicipalityHttpResponseStatusCode === 401 || getUserHttpResponseStatusCode === 401 || getParticipationsHttpResponseStatusCode === 401 || getSchoolsHttpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getMunicipalityHttpResponseStatusCode === 403 || getParticipationsHttpResponseStatusCode === 403 || getSchoolsHttpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  function handleFilterSelect(filter: FilterTypes) {
    setSelectedFilter((prevFilter) => (prevFilter === filter ? FilterTypes.None : filter));
  }

  function handleDownloadCsvClick(): void {
    window.open(
      `/api/paegnaben/municipality-administrator/statistics/score/municipalities/${municipalityGid}?legNumber=${selectedLegNumber}${
        selectedFilter !== FilterTypes.None ? `&filterType=${selectedFilter}` : ''
      }${schoolFilter ? `&schoolId=${schoolFilter.id}` : ''}`,
      '_blank',
    );
  }

  function handleTabChange(index: number): void {
    setActiveTabIndex(index);
    searchParams.set('t', index.toString());
    searchParams.delete('s');
    setSearchParams(searchParams);
  }

  function handleSelectSchoolFilterClick(school: ISchool): void {
    setSchoolFilter(school);
    handleTabChange(0);
    AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Skolfilter valt', school.name);
  }

  function handleDeleted(participation: IParticipationListEntryViewModel): void {
    setDeletedParticipation(participation);
    setParticipationDeleted(participationDeleted + 1);
  }

  async function handleRestoreSchoolButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    if (!schoolBackup) return;

    if (schoolBackup.municipalityGid === undefined) throw new Error('municipalityGid is required');
    const schoolApiModel: SchoolApiModel = {
      id: -1,
      name: schoolBackup.name,
      streetAddress: schoolBackup.streetAddress,
      postalCode: schoolBackup.postalCode,
      city: schoolBackup.city,
      municipalityGid: schoolBackup.municipalityGid,
    };

    try {
      const response = await fetch('/api/paegnaben/municipality-administrator/schools', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: JSON.stringify(schoolApiModel),
      });
      if (response.ok) {
        setSaveStatus(Status.success);
        setSaveErrorCode(undefined);
        setEdited(edited + 1);
        setSchoolBackup(undefined);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Skola återställd', '');
      } else {
        setSaveStatus(Status.failed);
        setSaveErrorCode(response.status);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Skola återställd - misslyckat', '');
      }
    } catch {
      setSaveStatus(Status.failed);
    }
  }

  async function handleDeleteSchoolButtonClick(schoolId: number) {
    if (saveStatus === Status.loading) return;
    searchParams.delete('s');
    setSearchParams(searchParams);
    const schoolToDelete = schools?.find((s) => s.id === schoolId);
    if (!schoolToDelete) return;
    setSchoolBackup(schoolToDelete);
    try {
      const response = await fetch(`/api/paegnaben/municipality-administrator/schools/${schoolId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });
      if (response.ok) {
        setEdited(edited + 1);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Skola borttagen', '');
      } else {
        setSaveStatus(Status.failed);
        setSaveErrorCode(response.status);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Skola borttagen - misslyckat', '');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setSaveStatus(Status.failed);
      setSaveErrorCode(error.status);
    }
  }

  function createDropDownMenuItems(school: ISchool): MenuItem[] {
    const menuItems: MenuItem[] = [
      {
        type: MenuItemType.Edit,
        label: 'Redigera',
        link: `/skolor/${school.id}`,
      },
    ];

    if (school.participations?.length === 0) {
      menuItems.push({
        type: MenuItemType.Delete,
        label: 'Ta bort',
        onClick: () => handleDeleteSchoolButtonClick(school.id),
      });
    }

    return menuItems;
  }

  function isParticipationListEntryViewModel(participation: IParticipation | IParticipationListEntryViewModel): participation is IParticipationListEntryViewModel {
    return (participation as IParticipationListEntryViewModel).participationId !== undefined;
  }

  // === RENDER ===
  if (getUserHttpResponseStatusCode === 401 || (saveStatus === Status.failed && saveErrorCode === 401)) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getUserHttpResponseStatusCode === 403 || (saveStatus === Status.failed && saveErrorCode === 403)) {
    return <Navigate to="/atkomst-saknas" />;
  }

  function renderInformationBoxes(): React.ReactNode {
    return (
      <div>
        {newlyRegisteredParticipation && (
          <InformationBox
            type={InformationBoxType.Success}
            headingElement={
              <h2 className="h4 mb-0">
                {newlyRegisteredParticipation?.participationName}, {newlyRegisteredParticipation?.schoolName} är nu anmäld till utmaningen
              </h2>
            }
            className="mb-4 mb-mb-5"
            setFocus={true}
          />
        )}
        {createdSchoolId && (
          <InformationBox
            type={InformationBoxType.Success}
            headingElement={<span className="fw-bold text-break">{schools?.find((x) => x.id === createdSchoolId)?.name} har lagts till</span>}
            closeCallback={() => {
              searchParams.delete('s');
              setSearchParams(searchParams);
            }}
            className="mb-4 mb-md-5"
            setFocus={true}
          />
        )}
        {savedSchoolId && (
          <InformationBox
            type={InformationBoxType.Success}
            headingElement={<span className="fw-bold text-break">Ändringar har sparats</span>}
            closeCallback={() => {
              searchParams.delete('ss');
              setSearchParams(searchParams);
            }}
            className="mb-4 mb-md-5"
            setFocus={true}
          />
        )}
        {schoolBackup && (
          <InformationBox
            type={InformationBoxType.Success}
            headingElement={<span className="fw-bold">{schoolBackup.name} har tagits bort</span>}
            textElement={
              <button className="btn btn-tertiary btn-icon-left btn-icon-arrow-u-turn-up-left" type="button" onClick={handleRestoreSchoolButtonClick}>
                Ångra
              </button>
            }
            closeCallback={() => setSchoolBackup(undefined)}
            className="mb-4 mb-md-5"
            setFocus={true}
          />
        )}
        {deletedParticipation && (
          <InformationBox
            type={InformationBoxType.Success}
            headingElement={
              <span className="fw-bold">
                {isParticipationListEntryViewModel(deletedParticipation)
                  ? `${deletedParticipation.participationName}, ${deletedParticipation.schoolName} `
                  : `${deletedParticipation.name}, ${deletedParticipation.school?.name} `}
                har tagits bort
              </span>
            }
            closeCallback={() => setDeletedParticipation(undefined)}
            className="mb-4 mb-md-5"
            setFocus={true}
          />
        )}
      </div>
    );
  }

  return (
    <div className="municipality">
      <Breadcumb
        items={[
          { text: 'Mina sidor', url: '/' },
          { text: municipality?.name ?? '', url: undefined },
        ]}
      />
      {municipality?.name && <PageIntroduction heading={municipality?.name} />}

      {loadingDataInProgress && <Spinner className="my-4 my-md-5" />}

      {!loadingDataInProgress && (
        <div>
          {renderInformationBoxes()}
          {isAllowedToRegister && (
            <div className="d-grid gap-2 d-sm-block mb-4 mb-md-5">
              <Link to={'anmalning'} className="btn btn-secondary">
                Anmäl klass
              </Link>
            </div>
          )}
          <TabList
            id="municipality-tab-list"
            activeIndex={activeTabIndex}
            renderTabsAsDropdownOnMobile={true}
            dropdownLabel={'Välj flik'}
            onSelectedChange={handleTabChange}
            navItems={
              showNavItems
                ? [<TabListNavItem key={'nav-item-0'}>Anmälda klasser ({participations?.length})</TabListNavItem>, <TabListNavItem key={'nav-item-2'}>Skolor ({schools?.length})</TabListNavItem>]
                : []
            }
            tabContents={[
              <TabListTabContent key={'tab-item-0'}>
                {!currentCompetition && <span>Ingen pågående tävling</span>}
                {currentCompetition && participations?.length === 0 && <span>Inga anmälda klasser</span>}
                {participations && participations.length > 0 && (
                  <div>
                    <div>
                      <label htmlFor="leg-select" className="form-label">
                        Välj etapp
                      </label>
                      <select
                        id="leg-select"
                        className="form-select mb-3"
                        value={selectedLegNumber === 0 ? 1 : selectedLegNumber}
                        onChange={(event) => {
                          handleLegSelectChange(event);
                          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Etapp vald', `Etapp ${event.target.value}`);
                        }}
                      >
                        {getLegOptions()}
                      </select>

                      {schoolFilter && (
                        <div className="school-filter-chips d-grid gap-2 d-sm-block mb-3 pb-3 border-bottom">
                          <SelectedFilterValue ariaLabel={schoolFilter.name} id="selected-filter-value" children={schoolFilter.name} onRemove={() => setSchoolFilter(undefined)} />
                        </div>
                      )}

                      {(anyLegFinsihed || anyLegIsActive) && (
                        <div className="row mb-3">
                          <div className="col-sm-6 mb-3 mb-sm-0 pe-sm-2">
                            <button
                              type="button"
                              className={`btn btn--as-filter w-100 ${selectedFilter === FilterTypes.PassedQualification ? 'active' : ''}`}
                              onClick={() => {
                                handleFilterSelect(FilterTypes.PassedQualification);
                                AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Klick på filterknapp', 'Klarat kvalgräns');
                              }}
                            >
                              Klarat kvalgräns ({participationsFilteredByPassedQualification?.length})
                            </button>
                          </div>
                          <div className="col-sm-6 ps-sm-2">
                            <button
                              type="button"
                              className={`btn btn--as-filter w-100 ${selectedFilter === FilterTypes.MissingLegPoints ? 'active' : ''}`}
                              onClick={() => {
                                handleFilterSelect(FilterTypes.MissingLegPoints);
                                AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Klick på filterknapp', 'Saknar etappoäng');
                              }}
                            >
                              Saknar etappoäng ({participationsFilteredByMissingLegPoints?.length})
                            </button>
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-tertiary btn-icon-left btn-icon-download mb-3 d-flex align-items-center"
                        onClick={() => {
                          handleDownloadCsvClick();
                          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Klick på `Hämta uppgifter för valda klasser (csv)`');
                        }}
                      >
                        Hämta uppgifter för valda klasser (csv)
                      </button>
                    </div>
                    <Participations participations={getParticipationsWithCurrentFilter()} anyLegIsActive={anyLegIsActive} onDeleted={handleDeleted} />
                  </div>
                )}
              </TabListTabContent>,
              <TabListTabContent key={'tab-item-2'}>
                {schools?.length === 0 && <p>Lägg till adressuppgifter till deltagande skolor</p>}
                {currentCompetition && (
                  <Link className="mb-3 d-inline-block school-button school-button--add" to={`/skolor/lagg-till?gid=${municipalityGid}`}>
                    Lägg till ny skola
                  </Link>
                )}
                <table className="table school-table">
                  <thead className="visually-hidden">
                    <tr>
                      <th scope="col">Namn</th>
                      <th scope="col">Åtgärder</th>
                      <th scope="col">Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSchoolsStatus === Status.loading && <Spinner className="my-4 my-md-5" />}
                    {schools?.map((school) => (
                      <tr key={school.id} className="d-flex justify-content-between">
                        <td className="text-break px-2 d-flex align-items-center flex-grow-1">
                          <div>
                            {school.name}&nbsp;
                            {school.participations && school.participations.length > 0 ? (
                              <button type="button" className="btn btn-link text-nowrap" onClick={() => handleSelectSchoolFilterClick(school)}>
                                ({school.participations.length})
                              </button>
                            ) : (
                              <span className="text-nowrap">({school.participations?.length})</span>
                            )}
                          </div>
                        </td>
                        <td className="school-table-row__menu p-0 d-flex align-items-center menu">
                          <ListItemDropDownMenu menuItems={createDropDownMenuItems(school)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {saveStatus === Status.failed && saveErrorCode !== 400 && (
                  <Alert show={true} heading="Kunde inte spara ändringarna" closable={false} className="mt-3" />
                )}
              </TabListTabContent>,
            ]}
          ></TabList>
        </div>
      )}
    </div>
  );
}

export default Municipality;